import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AdminMobileTopbar from "./AdminMobileTopbar";
import DateFilter from "../../common/DateFilter";
import { useAdminDashboard } from "./useAdminDashboard";

/**
 * Mobile version of admin/Dashboard.jsx. Same fetch/filter/sort logic
 * (via useAdminDashboard), same DateFilter already used on desktop -
 * only the layout (agent cards instead of a table) is mobile-specific.
 * "Nueva base" (CSV upload) is its own separate mobile screen, matching
 * the prototype - not embedded here like desktop's persistent sidebar.
 */
const MobileAdminDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchOpen, setSearchOpen] = useState(false);

  const { loading, searchTerm, setSearchTerm, sortedAgents, fetchAgents } = useAdminDashboard();

  useEffect(() => {
    fetchAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col h-screen bg-secondaryBg">
      <AdminMobileTopbar title={t("dashboard.dashboard")} />

      <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3">
        <div className="bg-white rounded-xl border border-gray-200 p-3">
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <DateFilter onDateRangeChange={(start, end) => fetchAgents(start, end)} />
            </div>
            <button
              type="button"
              onClick={() => setSearchOpen((prev) => !prev)}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 flex-shrink-0"
            >
              <svg viewBox="0 0 15 15" fill="none" width="14" height="14">
                <path d="M14.6259 12.8228L11.4545 9.66037C12.1097 8.67558 12.4933 7.49646 12.4933 6.22888C12.4933 2.79427 9.69104 0 6.24664 0C2.80224 0 0 2.79427 0 6.22888C0 9.66348 2.80224 12.4578 6.24664 12.4578C7.51783 12.4578 8.70032 12.0753 9.68792 11.4219L12.8593 14.5843C13.3466 15.0708 14.1386 15.0708 14.6259 14.5843C15.1137 14.0978 15.1137 13.3092 14.6259 12.8228ZM1.87399 6.22888C1.87399 3.82453 3.83544 1.86866 6.24664 1.86866C8.65784 1.86866 10.6193 3.82453 10.6193 6.22888C10.6193 8.63323 8.65784 10.5891 6.24664 10.5891C3.83544 10.5891 1.87399 8.63323 1.87399 6.22888Z" fill="#051D42" />
              </svg>
            </button>
          </div>
          {searchOpen && (
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("admin.option.selectAgent")}
              className="w-full mt-2 text-xs border border-gray-200 rounded-lg px-3 py-2"
            />
          )}
        </div>

        <p className="text-[11px] text-gray-500">
          {t("dashboard.uploadContact.showingFirst")} {sortedAgents.length}
        </p>

        {loading ? (
          <p className="text-xs text-gray-400 text-center py-8">{t("dashboard.loading")}</p>
        ) : sortedAgents.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-8">{t("dashboard.noAgents")}</p>
        ) : (
          sortedAgents.map((agent, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => agent.client_id && navigate(`/client-confirmation/${agent.client_id}`)}
              className="bg-white rounded-xl border border-gray-200 p-3.5 text-left"
            >
              <div className="mb-2.5">
                <p className="text-[13px] font-bold text-primary m-0">{agent.agent_name}</p>
                <p className="text-[11px] text-gray-400 m-0">{agent.company_name}</p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-secondaryBg rounded-lg p-2 text-center">
                  <p className="text-sm font-bold text-primary m-0">{agent.total_calls_made ?? "-"}</p>
                  <p className="text-[9px] text-gray-500 m-0">{t("dashboard.table.totalCalls")}</p>
                </div>
                <div className="bg-secondaryBg rounded-lg p-2 text-center">
                  <p className="text-sm font-bold text-primary m-0">{agent.total_calls_answered ?? "-"}</p>
                  <p className="text-[9px] text-gray-500 m-0">{t("dashboard.table.answered")}</p>
                </div>
                <div className="bg-secondaryBg rounded-lg p-2 text-center">
                  <p className="text-sm font-bold text-primary m-0">{agent.total_leads ?? "-"}</p>
                  <p className="text-[9px] text-gray-500 m-0">{t("dashboard.table.leads")}</p>
                </div>
                <div className="bg-secondary/10 rounded-lg p-2 text-center">
                  <p className="text-sm font-bold text-secondary m-0">{agent.total_meetings_booked ?? "-"}</p>
                  <p className="text-[9px] text-gray-500 m-0">{t("dashboard.table.meetings")}</p>
                </div>
                <div className="bg-secondaryBg rounded-lg p-2 text-center">
                  <p className="text-sm font-bold text-primary m-0">{agent.total_discarded ?? "-"}</p>
                  <p className="text-[9px] text-gray-500 m-0">{t("dashboard.table.outOfServ")}</p>
                </div>
                <div className="bg-secondaryBg rounded-lg p-2 text-center">
                  <p className="text-sm font-bold text-primary m-0">{agent.total_meetings_confirmed_attended ?? "-"}</p>
                  <p className="text-[9px] text-gray-500 m-0">{t("dashboard.table.success")}</p>
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
};

export default MobileAdminDashboard;
